"use client";

import React, { useState, useEffect } from "react";
import {
  DownloadCloudIcon,
  Edit,
  Plus,
  Trash2,
  TrashIcon,
  UploadIcon,
  X,
} from "lucide-react";
import BookingTable from "./common/Table";
import Button from "./Masters/Button";

import {
  useGetTourExclusionDetails,
  useAddTourExclusionDetails,
  useUpdateTourExclusionDetails,
  useTourExclusionStatus,
  useDeleteTourExclusions,
  useGetCountries,
  useTourBookingType,
} from "@/hooks/useCommon";
import { useAuth } from "@/store/auth";
import RTE from "./RTE";
import Swal from "sweetalert2";
import { toast } from "react-toastify";

interface TourExclusion {
  id: number;
  country_id: number;
  booking_type: number;
  title: string;
  description: string;
  status: number;
  created_by: number;
  modified_by: number | null;
  created_date: string;
  modified_date: string;
  ip: string | null;
  country_name?: string;
  package_name?: string;
  created_by_name?: string;
}

interface Country {
  id: number;
  name: string;
}

interface BookingType {
  id: number;
  tour_type: string; 
}

export const TourExclusion = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [currentexclusion, setCurrentexclusion] =
    useState<TourExclusion | null>(null);
  const [exclusions, setexclusions] = useState<TourExclusion[]>([]);
  const [selectedRows, setSelectedRows] = useState<number[]>([]);
  const [richTextContent, setRichTextContent] = useState<string>("");

  // Form states
  const [countryId, setCountryId] = useState<string>("");
  const [bookingType, setBookingType] = useState<string>("");
  const [title, setTitle] = useState<string>("");

  // API Hooks
  const getTourexclusions = useGetTourExclusionDetails();
  const addTourexclusion = useAddTourExclusionDetails();
  const updateTourexclusion = useUpdateTourExclusionDetails();
  const updateStatus = useTourExclusionStatus();
  const deleteTourexclusions = useDeleteTourExclusions();
  const countriesQuery = useGetCountries();
  const bookingTypesQuery = useTourBookingType();

  const user = useAuth();
  const loggedInUser = user?.user?.id;

 
  const countries: Country[] = countriesQuery.data || [];
  const bookingTypes: BookingType[] = bookingTypesQuery.data?.data || [];

  // Extract and set tour exclusions from query data
  useEffect(() => {
    if (getTourexclusions.data) {
      // The hook returns res?.data?.responseData?.response
      // So getTourexclusions.data = { status: "success", data: [...], message: "..." }
      const responseData = getTourexclusions.data;
      let exclusionsData: TourExclusion[] = [];
      
      // Check different possible response structures
      if (responseData?.data && Array.isArray(responseData.data)) {
        exclusionsData = responseData.data;
      } else if (responseData?.responseData?.response?.data && Array.isArray(responseData.responseData.response.data)) {
        exclusionsData = responseData.responseData.response.data;
      } else if (Array.isArray(responseData)) {
        exclusionsData = responseData;
      }
      
      setexclusions(exclusionsData);
    } else {
      // Reset to empty array if no data
      setexclusions([]);
    }
  }, [getTourexclusions.data]);

  // Reset states when modal opens/closes
  useEffect(() => {
    if (!isModalOpen) {
      setCurrentexclusion(null);
      setCountryId("");
      setBookingType("");
      setTitle("");
      setRichTextContent("");
    }
  }, [isModalOpen]);

  // Set form data when currentexclusion changes (for editing)
  useEffect(() => {
    if (currentexclusion && isModalOpen) {
      setCountryId(currentexclusion.country_id?.toString() || "");
      setBookingType(currentexclusion.booking_type?.toString() || "");
      setTitle(currentexclusion.title || "");
      setRichTextContent(currentexclusion.description || "");
    }
  }, [currentexclusion, isModalOpen]);

  const handleFormSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate required fields
    if (!countryId) {
      toast.warn("Please select a country");
      return;
    }
    if (!bookingType) {
      toast.warn("Please select a booking type");
      return;
    }
    if (!title.trim()) {
      toast.warn("Please enter a title");
      return;
    }
    if (!richTextContent.trim()) {
      toast.error("Please enter a description");
      return;
    }

    try {
      const payload = {
        country_id: parseInt(countryId),
        booking_type: parseInt(bookingType),
        title: title.trim(),
        description: richTextContent,
        status: 1,
        ip: "127.0.0.1",
        created_by: loggedInUser,
        modified_by: loggedInUser,
      };

      if (currentexclusion) {
        // Update existing tour exclusion
        updateTourexclusion.mutate(
          {
            ...payload,
            auto_id: currentexclusion.id,
          },
          {
            onSuccess: (data) => {
              if (data?.status === "success" || data?.responseData?.response?.status === "success") {
                getTourexclusions.refetch();
                setIsModalOpen(false);
                setCurrentexclusion(null);
                toast.success("Tour exclusion updated successfully");
              }
            },
            onError: (error) => {
              toast.error("Failed to update tour exclusion. Please try again.");
            },
          }
        );
      } else {
        // Add new tour exclusion
        addTourexclusion.mutate(payload, {
          onSuccess: (data) => {
            if (data?.status === "success" || data?.responseData?.response?.status === "success") {
              getTourexclusions.refetch();
              setIsModalOpen(false);
              toast.success("Tour exclusion added successfully");
            }
          },
          onError: (error) => {
            toast.error("Failed to add tour exclusion. Please try again.");
          },
        });
      }
    } catch (error) {
      toast.error("An error occurred. Please try again.");
    }
  };

  const handlePublish = () => {
    if (selectedRows.length === 0) return;

    updateStatus.mutate(
      {
        id: selectedRows.join(","),
        user_id: loggedInUser || 0,
        status: 1,
      },
      {
        onSuccess: (data) => {
          if (data?.status === "success" || data?.responseData?.response?.status === "success") {
            getTourexclusions.refetch();
            setSelectedRows([]);
            toast.success("Tour exclusions published successfully");
          }
        },
        onError: (error) => {
          toast.error("Failed to publish tour exclusions. Please try again.");
        },
      }
    );
  };

  const handleUnpublish = () => {
    if (selectedRows.length === 0) return;

    updateStatus.mutate(
      {
        id: selectedRows.join(","),
        user_id: loggedInUser || 0,
        status: 0,
      },
      {
        onSuccess: (data) => {
          if (data?.status === "success" || data?.responseData?.response?.status === "success") {
            getTourexclusions.refetch();
            setSelectedRows([]);
            toast.success("Tour exclusions unpublished successfully");
          }
        },
        onError: (error) => {
          toast.error("Failed to unpublish tour exclusions. Please try again.");
        },
      }
    );
  };

  const handleDelete = async () => {
    if (selectedRows.length === 0) return;

    const message =
      selectedRows.length === 1
        ? "Are you sure you want to delete this tour exclusion?"
        : `Are you sure you want to delete ${selectedRows.length} selected tour exclusions?`;

    const result = await Swal.fire({
      title: "Confirm delete",
      text: message,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, delete",
    });

    if (!result.isConfirmed) return;

    deleteTourexclusions.mutate(
      { id: selectedRows.join(","), user_id: loggedInUser },
      {
        onSuccess: (data) => {
          if (data?.status === "success" || data?.responseData?.response?.status === "success") {
            getTourexclusions.refetch();
            setSelectedRows([]);
            toast.success("Tour exclusion(s) deleted successfully");
          }
        },
        onError: () => {
          Swal.fire(
            "Error",
            "Failed to delete tour exclusion(s). Please try again.",
            "error"
          );
        },
      }
    );
  };
  const tableData = exclusions.map((item, index) => ({
    sno: index + 1,
    id: item.id,
    country: item.country_name || `Country ${item.country_id}`,
    bookingType: item.package_name || `Type ${item.booking_type}`,
    title: item.title,
    description: item.description,
    status: item.status,
    createdBy: item.created_by_name,
    createdDate: item.created_date,
    originalData: item,
  }));

  const columns = [
    {
      key: "selection",
      header: "",
      sortable: false,
      render: (_: any, row: any) => (
        <input
          type="checkbox"
          checked={selectedRows.includes(row.id)}
          onChange={(e) => {
            if (e.target.checked) {
              setSelectedRows([...selectedRows, row.id]);
            } else {
              setSelectedRows(selectedRows.filter((id) => id !== row.id));
            }
          }}
          className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
        />
      ),
    },
    { key: "sno", header: "S.No", sortable: true },
    { key: "country", header: "Country", sortable: true },
    { key: "bookingType", header: "Booking Type", sortable: true },
    {
      key: "title",
      header: "Title",
      sortable: true,
      width: "200px",
      render: (title: string) => (
        <div className="max-w-[200px] truncate" title={title}>
          {title}
        </div>
      ),
    },
    {
      key: "description",
      header: "Description",
      sortable: true,
      width: "300px",
      render: (description: string) => (
        <div
          className="max-w-[300px] overflow-hidden text-ellipsis line-clamp-3"
          dangerouslySetInnerHTML={{ __html: description }}
        />
      ),
    },
    {
      key: "status",
      header: "Status",
      sortable: true,
      render: (item: any) => {
        return (
          <div className="flex items-center">
            <div
              className={`w-3 h-3 rounded-full mr-2 ${
                item === 1 ? "bg-green-500" : "bg-red-500"
              }`}
            />
            <span className="text-sm">
              {item === 1 ? "Active" : "Inactive"}
            </span>
          </div>
        );
      },
    },
    {
      key: "createdBy",
      header: "Created By",
      sortable: true,
      render: (userId: number) => `${userId}`,
    },
    {
      key: "createdDate",
      header: "Created Date",
      sortable: true,
      render: (date: string) => new Date(date).toLocaleDateString(),
    },
    {
      key: "action",
      header: "Action",
      render: (_: any, row: any) => {
        return (
          <div className="flex space-x-2">
            <button
              className="text-blue-500 hover:text-blue-700"
              onClick={() => {
                setCurrentexclusion(row.originalData);
                setIsModalOpen(true);
              }}
            >
              <Edit size={15} />
            </button>
           
            <button
              className="text-red-500 hover:text-red-700"
              onClick={async () => {
                const result = await Swal.fire({
                  title: "Confirm delete",
                  text: "Are you sure you want to delete this tour exclusion?",
                  icon: "warning",
                  showCancelButton: true,
                  confirmButtonText: "Yes, delete",
                });

                if (!result.isConfirmed) return;

                // Delete single tour exclusion directly
                deleteTourexclusions.mutate(
                  {
                    id: row.id.toString(),
                    user_id: loggedInUser || 0,
                  },
                  {
                    onSuccess: (data) => {
                      if (data?.status === "success" || data?.responseData?.response?.status === "success") {
                        getTourexclusions.refetch();
                        toast.success("Tour exclusion deleted successfully");
                      }
                    },
                    onError: () => {
                      Swal.fire(
                        "Error",
                        "Failed to delete tour exclusion. Please try again.",
                        "error"
                      );
                    },
                  }
                );
              }}
            >
              <Trash2 size={15} />
            </button>
          </div>
        );
      },
    },
  ];

  // Select all checkbox handler
  const handleSelectAll = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.checked) {
      setSelectedRows(tableData.map((row) => row.id));
    } else {
      setSelectedRows([]);
    }
  };

 
  const formFields: any = [
    {
      id: "country_id",
      label: "Country *",
      type: "select",
      required: true,
      defaultValue: countryId,
      onChange: (value: string) => setCountryId(value),
      options: countries.map((country) => ({
        value: country.id.toString(),
        label: country.name,
      })),
      loading: countriesQuery.isLoading,
    },
    {
      id: "booking_type",
      label: "Booking Type *",
      type: "select",
      required: true,
      defaultValue: bookingType,
      onChange: (value: string) => setBookingType(value),
      options: bookingTypes.map((type) => ({
        value: type.id.toString(),
        label: type.tour_type, 
      })),
      loading: bookingTypesQuery.isLoading,
    },
    {
      id: "title",
      label: "Title *",
      type: "text",
      required: true,
      defaultValue: title,
      onChange: (value: string) => setTitle(value),
      placeholder: "Enter title",
    },
  ];

  return (
    <div className="bg-white rounded-lg shadow border border-gray-200 p-6">
      <div className="mt-5">
        <div className="flex justify-end gap-2">
          <Button
            size="sm"
            onClick={() => {
              setCurrentexclusion(null);
              setIsModalOpen(true);
            }}
            icon={<Plus className="w-4 h-4" />}
            iconPosition="left"
          >
            Add Tour exclusion
          </Button>

          <Button
            size="sm"
            onClick={handlePublish}
            icon={<UploadIcon className="w-4 h-4" />}
            iconPosition="left"
            disabled={selectedRows.length === 0 || updateStatus.isPending}
          >
            {updateStatus.isPending ? "Publishing..." : "Publish"}
          </Button>

          <Button
            size="sm"
            onClick={handleUnpublish}
            icon={<DownloadCloudIcon className="w-4 h-4" />}
            iconPosition="left"
            disabled={selectedRows.length === 0 || updateStatus.isPending}
          >
            {updateStatus.isPending ? "Unpublishing..." : "Unpublish"}
          </Button>

          <Button
            size="sm"
            variant="danger"
            onClick={handleDelete}
            icon={<TrashIcon className="w-4 h-4" />}
            iconPosition="left"
            disabled={
              selectedRows.length === 0 || deleteTourexclusions.isPending
            }
          >
            {deleteTourexclusions.isPending ? "Deleting..." : "Delete"}
          </Button>
        </div>

        <BookingTable
          data={tableData}
          columns={columns}
          heading={[]}
          searchable={true}
          filterable={true}
          sortable={true}
          selectable={true}
          onSelectionChange={setSelectedRows}
          loading={
            getTourexclusions.isPending ||
            countriesQuery.isLoading ||
            bookingTypesQuery.isLoading
          }
          renderHeader={() => (
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  <input
                    type="checkbox"
                    checked={
                      selectedRows.length === tableData.length &&
                      tableData.length > 0
                    }
                    onChange={handleSelectAll}
                    className="h-4 w-4 rounded border-gray-300 text-blue-600 focus:ring-blue-500"
                  />
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  S.No
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Country
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Booking Type
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Title
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Description
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created By
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Created Date
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Action
                </th>
              </tr>
            </thead>
          )}
        />
      </div>

      {isModalOpen && (
        <div className="fixed inset-0 z-50 overflow-y-auto">
          <div
            className="fixed inset-0 bg-black/50 backdrop-blur-sm transition-opacity duration-300"
            onClick={() => {
              setIsModalOpen(false);
              setCurrentexclusion(null);
            }}
          />

          <div className="flex items-center justify-center min-h-screen p-2 sm:p-4">
            <div
              className="relative bg-white rounded-lg sm:rounded-xl shadow-xl w-full max-w-4xl mx-2 sm:mx-4 my-4 sm:my-8 max-h-[95vh] sm:max-h-[90vh] flex flex-col transform transition-all duration-300"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="flex items-center justify-between px-4 py-3 sm:px-5 sm:py-4 border-b border-gray-200 flex-shrink-0">
                <h2 className="text-lg sm:text-xl font-semibold text-gray-900 truncate pr-2">
                  {currentexclusion
                    ? "Update Tour exclusion"
                    : "Add Tour exclusion"}
                </h2>

                <button
                  onClick={() => {
                    setIsModalOpen(false);
                    setCurrentexclusion(null);
                  }}
                  className="group p-1.5 sm:p-2 rounded-full hover:bg-gray-100 focus:outline-none focus:ring-2 focus:ring-blue-500 transition-all duration-200 flex-shrink-0"
                  aria-label="Close modal"
                >
                  <X className="h-4 w-4 sm:h-5 sm:w-5 text-gray-500 group-hover:text-gray-700 transition-colors" />
                </button>
              </div>

              <div className="px-4 py-3 sm:px-5 sm:py-4 overflow-y-auto flex-1 min-h-0">
                <form onSubmit={handleFormSubmit} className="space-y-4">
                  {/* Dynamic Form Fields */}
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                    {formFields.map((field: any) => (
                      <div key={field.id} className="space-y-2">
                        <label className="block text-sm font-medium text-gray-700">
                          {field.label}
                        </label>
                        {field.type === "select" ? (
                          <div className="relative">
                            <select
                              value={field.defaultValue || ""}
                              onChange={(e) => field.onChange(e.target.value)}
                              required={field.required}
                              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                              disabled={field.loading}
                            >
                              <option value="">
                                Select {field.label.replace("*", "")}
                              </option>
                              {field.options.map((option: any) => (
                                <option key={option.value} value={option.value}>
                                  {option.label}
                                </option>
                              ))}
                            </select>
                            {field.loading && (
                              <div className="absolute right-3 top-2.5">
                                <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-blue-500"></div>
                              </div>
                            )}
                          </div>
                        ) : (
                          <input
                            type={field.type}
                            value={field.defaultValue || ""}
                            onChange={(e) => field.onChange(e.target.value)}
                            placeholder={field.placeholder}
                            required={field.required}
                            className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                          />
                        )}
                      </div>
                    ))}
                  </div>

                  {/* Rich Text Editor for Description */}
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700">
                      Description *
                    </label>
                    <RTE
                      content={richTextContent}
                      onChange={setRichTextContent}
                    />
                  </div>

                  {/* Submit Button */}
                  <div className="flex justify-end pt-4">
                    <button
                      type="submit"
                      disabled={
                        addTourexclusion.isPending ||
                        updateTourexclusion.isPending ||
                        countriesQuery.isLoading ||
                        bookingTypesQuery.isLoading
                      }
                      className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed"
                    >
                      {addTourexclusion.isPending ||
                      updateTourexclusion.isPending
                        ? "Processing..."
                        : currentexclusion
                        ? "Update Tour exclusion"
                        : "Add Tour exclusion"}
                    </button>
                  </div>
                </form>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default TourExclusion;
